import { SearchResultTeaser } from '@xref/xref-lib';

/** Wire values from api.xref.io POST /xme/search — same as @xref/xref-lib SearchResultMatchCase. */
export type XmeMatchCase = 'new' | 'locked' | 'unlocked';

/** Wire values from api.xref.io Xme unlock lifecycle responses. */
export type XmeUnlockStatus = 'requested' | 'expired' | 'ended';

export function isXmeRequestedUnlockStatus(
  unlockStatus?: string | null,
): unlockStatus is 'requested' {
  return unlockStatus === 'requested';
}

export interface XmeTeaser {
  name: string;
  title?: string;
  initials?: string;
  profile_picture_url?: string | null;
  matched_email?: string;
  city?: string | null;
  country?: string | null;
  country_flag?: string | null;
  career_coverage_label?: string | null;
  employment_verifications_count?: number;
  supporting_documents_count?: number;
  id_verified?: boolean;
  work_entitlement_verified?: boolean;
  teaser_timeline?: Array<{ kind: string; start_date?: string; end_date?: string; flex?: number }>;
  profile_url?: string | null;
}

export interface XmeSearchResponse {
  match: boolean;
  case?: XmeMatchCase | null;
  teaser?: XmeTeaser | null;
  person_uid?: string | null;
  xme_user_id?: string | null;
  unlock_status?: XmeUnlockStatus | null;
  credits_available?: number;
  can_request?: boolean;
  can_view?: boolean;
}

export interface XmeRequestVerificationsResponse {
  person_uid: string;
  person_created: boolean;
  xme_access_uid: string;
  xme_access_request_id: number;
  status: string;
  pending_expires_at?: string;
}

export interface XmeViewerSessionResponse {
  viewer_url?: string;
  status?: string;
}

export interface XmeStopResponse {
  status?: string;
  xme_access_uid?: string;
}

export interface XmeAccessIds {
  person_uid?: string | null;
  xme_access_uid?: string | null;
}

/** App-side extension consumed by xref-lib 1.4.11+. */
export interface XmeSearchResultTeaser extends SearchResultTeaser {
  timelineStartDateLabel?: string | null;
  timelineEndDateLabel?: string | null;
}

const TODAY_LABEL = $localize`Today`;

/** api.xref.io speaks snake_case; the library card expects camelCase. */
export function toSearchResultTeaser(teaser: XmeTeaser): XmeSearchResultTeaser {
  // Parity with xref.me's ProfileBannerTimelineBar: the bar always runs to the current
  // month, so it labels the first month on the left and the literal "Today" on the
  // right. api.xref.me emits the same leading month run, so mirror it rather than
  // deriving an end date the banner never shows.
  const timeline = teaser.teaser_timeline || [];

  return {
    name: teaser.name,
    title: teaser.title,
    initials: teaser.initials,
    profilePictureUrl: teaser.profile_picture_url,
    matchedEmail: teaser.matched_email,
    city: teaser.city,
    country: teaser.country,
    countryFlag: teaser.country_flag,
    // Empty/missing coverage must render as 0 — never a blank stat cell.
    careerCoverageLabel: teaser.career_coverage_label?.trim() || '0',
    employmentVerificationsCount: teaser.employment_verifications_count ?? 0,
    supportingDocumentsCount: teaser.supporting_documents_count ?? 0,
    idVerified: teaser.id_verified,
    workEntitlementVerified: teaser.work_entitlement_verified,
    teaserTimeline: (teaser.teaser_timeline || []).map(segment => ({
      kind: segment.kind,
      startDate: segment.start_date,
      endDate: segment.end_date,
      flex: segment.flex,
    })),
    timelineStartDateLabel: timeline[0]?.start_date?.trim() || null,
    timelineEndDateLabel: timeline.length ? TODAY_LABEL : null,
    profileUrl: teaser.profile_url,
  };
}
